import React, { useState, useEffect } from "react";
import { Autocomplete } from "@react-google-maps/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { CONSTANTS } from "../contants/constants";

const GooglePlacesAutocomplete = ({
  section,
  field,
  isMandatory,
  setPlaceId,
  onAddressSelect,
}) => {
  const [autocompleteInstance, setAutocompleteInstance] = useState(null);
  const [addressValue, setAddressValue] = useState("");
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(null);

  useEffect(() => {
    // Check if script is already loaded
    if (window.google && window.google.maps) {
      setIsScriptLoaded(true);
      return;
    }

    // Load Google Maps script
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${CONSTANTS.GOOGLE_MAPS_API}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      setIsScriptLoaded(true);
    };

    script.onerror = () => {
      setScriptError("Failed to load Google Maps script");
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup
      document.head.removeChild(script);
    };
  }, []);

  const handlePlaceSelect = () => {
    if (autocompleteInstance) {
      const place = autocompleteInstance.getPlace();
      console.log("Selected place:", place);

      if (place && place.place_id) {
        setPlaceId(place.place_id);
        setAddressValue(place.formatted_address);

        // Initialize address components
        const addressComponents = {
          streetNumber: "",
          streetName: "",
          suburb: "",
          city: "",
          province: "",
          postalCode: "",
          country: "",
        };

        if (place.address_components) {
          // Process address components
          place.address_components.forEach((component) => {
            const types = component.types;

            if (types.includes("street_number")) {
              addressComponents.streetNumber = component.long_name;
            }
            if (types.includes("route")) {
              addressComponents.streetName = component.long_name;
            }
            if (
              types.includes("sublocality_level_1") ||
              types.includes("sublocality")
            ) {
              addressComponents.suburb = component.long_name;
            }
            if (types.includes("locality")) {
              addressComponents.city = component.long_name;
              // If no suburb is set, use locality as suburb
              if (!addressComponents.suburb) {
                addressComponents.suburb = component.long_name;
              }
            }
            if (types.includes("administrative_area_level_1")) {
              addressComponents.province = component.long_name;
            }
            if (types.includes("postal_code")) {
              addressComponents.postalCode = component.long_name;
            }
            if (types.includes("country")) {
              // Get the full country name
              addressComponents.country = component.long_name;
              console.log("Found country:", component.long_name); // Keep this log for debugging
            }
          });

          // Call the callback with address components
          if (onAddressSelect) {
            onAddressSelect(addressComponents);
          }
        }
      }
    }
  };
  const handleClearAddress = () => {
    setAddressValue("");
    setPlaceId("");
    if (onAddressSelect) {
      onAddressSelect({
        streetNumber: "",
        streetName: "",
        suburb: "",
        city: "",
        province: "",
        postalCode: "",
        country: "",
      });
    }
  };

  if (scriptError) {
    return (
      <div className="text-red-500 text-sm">
        Error loading Google Maps: {scriptError}
      </div>
    );
  }

  if (!isScriptLoaded) {
    return (
      <div className="w-full h-10 sm:h-12 px-3 sm:px-4 text-sm sm:text-base rounded-full border border-gray-300 bg-gray-100 animate-pulse" />
    );
  }

  return (
    <div className="relative">
      <Autocomplete
        onLoad={(autocomplete) => {
          setAutocompleteInstance(autocomplete);
        }}
        onPlaceChanged={handlePlaceSelect}
        options={{
          fields: ["address_components", "formatted_address", "place_id"],
        }}
      >
        <div className="relative">
          <input
            type="text"
            placeholder="Type and select an address from the dropdown"
            value={addressValue}
            onChange={(e) => {
              setAddressValue(e.target.value);
            }}
            className="w-full h-10 sm:h-12 px-3 sm:px-4 text-sm sm:text-base rounded-full border border-gray-300 focus:ring-blue-500 focus:border-transparent"
          />
          {addressValue && (
            <button
              type="button"
              onClick={handleClearAddress}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Clear address"
            >
              <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
            </button>
          )}
        </div>
      </Autocomplete>
    </div>
  );
};

export default GooglePlacesAutocomplete;
